import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LargeTitle, Surface, Skeleton, EmptyState, Pill } from "@/components/ios";
import { ScrollText, Search, EyeOff } from "lucide-react";
import { toast } from "sonner";

const ACTION_TONE = { sign: "success", issue: "accent", generate: "accent", create: "neutral", review_item: "warning", send_notice: "accent", verify: "success", tenant_respond: "success", media_access: "neutral", pii_redact: "warning", privacy_redact: "danger" };

export default function AuditLog() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [q, setQ] = useState("");
  const canRedact = ["principal", "admin"].includes(user?.role);
  const load = () => api.get("/audit").then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);

  const redact = async (id) => {
    try { await api.post(`/audit/${id}/redact`); toast.success("Payload cryptographically erased"); load(); }
    catch (e) { toast.error("Redaction failed"); }
  };

  const filtered = (events || []).filter((e) =>
    !q || `${e.action} ${e.entity_type} ${e.actor_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <LargeTitle subtitle="Append-only record · payloads encrypted, redaction via cryptographic erasure">Audit Log</LargeTitle>
      <div className="relative mb-4 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search actions…" data-testid="audit-search"
          className="w-full text-sm rounded-xl pl-9 pr-3 py-2.5 outline-none border" style={{ background: "var(--surface)", borderColor: "var(--border)", color: "var(--text)" }} />
      </div>
      {!events ? <div className="space-y-2">{[0,1,2,3].map(i => <Skeleton key={i} className="h-12" />)}</div>
        : filtered.length === 0 ? <Surface><EmptyState icon={ScrollText} title="No events" /></Surface>
        : <Surface className="overflow-hidden">
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {filtered.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid={`audit-${e.id}`}>
                <Pill tone={ACTION_TONE[e.action] || "neutral"}>{e.action.replace(/_/g, " ")}</Pill>
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate" style={{ color: "var(--text)" }}>
                    <span className="font-medium">{e.actor_name}</span> · {e.entity_type} {e.entity_id?.slice(0, 8)}
                    {e.meta?._redacted && <span className="ml-2 text-xs" style={{ color: "var(--danger)" }}>· payload redacted</span>}
                  </div>
                  <div className="text-xs text-secondary">{new Date(e.at).toLocaleString("en-AU")}{e.ip ? ` · ${e.ip}` : ""}</div>
                </div>
                {canRedact && !e.meta?._redacted && (
                  <button data-testid={`redact-${e.id}`} onClick={() => redact(e.id)} className="flex items-center gap-1 text-xs font-semibold text-secondary hover:opacity-70" title="Cryptographically erase payload">
                    <EyeOff size={13} /> Redact
                  </button>
                )}
              </div>
            ))}
          </div>
        </Surface>}
    </div>
  );
}
